import { EMOTE_ACTIONS, EMOTE_EMOJI, EMOTE_KINDS, EMOTE_PAGES, MAX_EMOTE_ID, entitledEmotes, type EmoteKind, type EmotePage } from "odyssey-shared";

/**
 * The emote wheel's rules as pure functions (P2.19-T8, P2.19-T9; ADR-114 decisions 1.2, 1.3, 2.2 and 6.3): the pages
 * an account's sets give, where a page key and the mouse wheel turn the page, and the page and entry kept per device.
 * `store.ts` holds the wheel's state over them.
 */

/**
 * The storage key the page and entry last sent from are kept under.
 */
export const EMOTES_LAST_KEY = "odyssey.emotes.last";

/**
 * How much of a mouse wheel's delta turns the page once: a notch of a wheel that counts pixels, and what a touchpad's
 * stream of small deltas must add up to.
 */
export const WHEEL_NOTCH = 100;

/**
 * The pages the wheel shows, in the order its page keys turn them: the Emoji page, then the five pages of actions
 * (ADR-114 decision 1.2), so a device that kept nothing opens on Emoji.
 */
export const WHEEL_PAGE_NAMES: readonly EmotePage[] = EMOTE_PAGES;

/**
 * The longest stored text read as a kept place; anything longer was not written by the game.
 */
const MAX_LAST_LENGTH = 128;

/**
 * One entry of a page: the emote's kind and id as the wire names it, the word on its button or an emoji's name, and
 * for an emoji the icon key of its art (ADR-111), null for an action, which is drawn as its word.
 */
export interface WheelEntry {
  readonly kind: EmoteKind;
  readonly id: number;
  readonly word: string;
  readonly key: string | null;
}

/**
 * One page: its name and the entries the account holds, in id order.
 */
export interface WheelPage {
  readonly name: EmotePage;
  readonly entries: readonly WheelEntry[];
}

/**
 * The page and entry the wheel last sent from: the page's name and the entry's kind and id.
 */
export interface KeptPlace {
  readonly page: EmotePage;
  readonly kind: EmoteKind;
  readonly id: number;
}

/**
 * A place in the wheel: a page by its index among the pages shown and an entry by its index on that page.
 */
export interface WheelPlace {
  readonly page: number;
  readonly entry: number;
}

/**
 * The pages of the wheel for an account: each named page with the entries its sets hold, in id order. An entry
 * outside the sets is not shown, and a page that would hold none is left out.
 *
 * @param sets The set names the gateway sent for the account.
 * @param names The pages to show, in order; the wheel's unless given.
 * @returns The pages.
 * @example
 * wheelPages(["base"]).map((page) => page.name);
 * // ["Emoji", "Greetings", "Emotions", "Actions", "Interactions", "Duel"]
 */
export function wheelPages(sets: readonly string[], names: readonly EmotePage[] = WHEEL_PAGE_NAMES): readonly WheelPage[] {
  const held = entitledEmotes(sets);
  const pages: WheelPage[] = [];
  for (const name of names) {
    const entries: WheelEntry[] = [];
    for (const emoji of EMOTE_EMOJI) {
      if (emoji.page === name && held.emoji.has(emoji.id)) {
        entries.push({ kind: "emoji", id: emoji.id, word: emoji.name, key: emoji.key });
      }
    }
    for (const action of EMOTE_ACTIONS) {
      if (action.page === name && held.actions.has(action.id)) {
        entries.push({ kind: "action", id: action.id, word: action.word, key: null });
      }
    }
    if (entries.length > 0) {
      pages.push({ name, entries });
    }
  }
  return pages;
}

/**
 * The page a page key turns to: the one after or before, the last wrapping to the first and the first to the last.
 *
 * @param page The page's index.
 * @param step 1 for the page after, -1 for the one before.
 * @param count How many pages the wheel shows.
 * @returns The index turned to; the same index when there are no pages.
 * @example
 * turnPage(5, 1, 6);
 * // 0
 */
export function turnPage(page: number, step: 1 | -1, count: number): number {
  return count <= 0 ? page : (((page + step) % count) + count) % count;
}

/**
 * A mouse wheel event's turn as pixels of delta. A wheel that counts pixels gives its own delta, so a touchpad's small
 * deltas add up; one that counts lines or pages gives a whole notch an event, whatever the size it reports.
 *
 * @param event The event's vertical delta and its unit (`WheelEvent.deltaMode`: 0 pixels, 1 lines, 2 pages).
 * @returns The delta in pixels, positive downward; 0 for an event that turned nothing.
 * @example
 * wheelDelta({ deltaY: -3, deltaMode: 1 });
 * // -100
 */
export function wheelDelta(event: { readonly deltaY: number; readonly deltaMode: number }): number {
  if (!Number.isFinite(event.deltaY) || event.deltaY === 0) {
    return 0;
  }
  return event.deltaMode === 0 ? event.deltaY : Math.sign(event.deltaY) * WHEEL_NOTCH;
}

/**
 * How many pages a delta turns, with what is left over for the next event: one page for each `WHEEL_NOTCH` of
 * delta. A delta against what was carried drops the carry, so a turn of direction starts its count
 * again.
 *
 * @param carry What earlier events left over, in pixels.
 * @param delta This event's delta in pixels, positive downward.
 * @returns The whole pages to turn, positive for later ones, and the new carry. No step is 0 and never the
 *   negative zero a small upward delta truncates to, which a caller comparing steps would read as a second value.
 * @example
 * wheelSteps(90, 30);
 * // { steps: 1, carry: 20 }
 */
export function wheelSteps(carry: number, delta: number): { readonly steps: number; readonly carry: number } {
  const total = (delta !== 0 && Math.sign(delta) !== Math.sign(carry) ? 0 : carry) + delta;
  const steps = Math.trunc(total / WHEEL_NOTCH) + 0;
  return { steps, carry: total - steps * WHEEL_NOTCH };
}

/**
 * Reads the place a device kept.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The place, or null for anything that is not a page of the emote table with a kind and an id an emote may
 *   have. Whether the account still holds the entry is `openingPlace`'s.
 * @example
 * parseLast('{"page":"Duel","kind":"action","id":23}');
 * // { page: "Duel", kind: "action", id: 23 }
 */
export function parseLast(raw: string | null): KeptPlace | null {
  if (raw === null || raw.length > MAX_LAST_LENGTH) {
    return null;
  }
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof stored !== "object" || stored === null || Array.isArray(stored)) {
    return null;
  }
  const { page, kind, id } = stored as { readonly page?: unknown; readonly kind?: unknown; readonly id?: unknown };
  const pageName = (EMOTE_PAGES as readonly string[]).includes(page as string) ? (page as EmotePage) : null;
  const emoteKind = (EMOTE_KINDS as readonly string[]).includes(kind as string) ? (kind as EmoteKind) : null;
  if (pageName === null || emoteKind === null || typeof id !== "number" || !Number.isInteger(id) || id < 1 || id > MAX_EMOTE_ID) {
    return null;
  }
  return { page: pageName, kind: emoteKind, id };
}

/**
 * A place as it is kept.
 *
 * @param place The place.
 * @returns The text to store.
 * @example
 * formatLast({ page: "Duel", kind: "action", id: 23 });
 * // '{"page":"Duel","kind":"action","id":23}'
 */
export function formatLast(place: KeptPlace): string {
  return JSON.stringify({ page: place.page, kind: place.kind, id: place.id });
}

/**
 * Where the wheel opens: the kept page with the kept entry selected, or the first page on its first entry when
 * nothing is kept or the kept entry is one the account no longer holds on that page.
 *
 * @param pages The pages the account's sets give.
 * @param kept The place kept, or null.
 * @returns The place to open at.
 * @example
 * openingPlace(wheelPages(["base"]), { page: "Interactions", kind: "action", id: 22 });
 * // { page: 4, entry: 5 }
 */
export function openingPlace(pages: readonly WheelPage[], kept: KeptPlace | null): WheelPlace {
  if (kept !== null) {
    const page = pages.findIndex((one) => one.name === kept.page);
    const entry = page === -1 ? -1 : (pages[page] as WheelPage).entries.findIndex((one) => one.kind === kept.kind && one.id === kept.id);
    if (entry !== -1) {
      return { page, entry };
    }
  }
  return { page: 0, entry: 0 };
}
